import { drag, scaleLinear, select } from "d3";
import { clamp } from "lodash";
import React, { MouseEventHandler, useEffect, useRef } from "react";

import { useElementResponsiveDimensions } from "../../hooks";
import { DragDivElement } from "../../types";
import styles from "./play-control.module.scss";

export interface PlayControlProps {
  time: number;
  maxTime: number;
  setTime: (time: number) => void;
  pause: () => void;
}

export const PlayControl = ({ time, maxTime, setTime, pause }: PlayControlProps) => {
  /** Get the dynamic width of the player track */
  const trackContainerRef = useRef<HTMLDivElement>(null);
  const { width } = useElementResponsiveDimensions(trackContainerRef);

  /** Create time scale using width of play track */
  const timeScale = scaleLinear()
    .domain([0, maxTime])
    .range([0, width - 16]);

  /** set the time when clicking on the timeline axis */
  const handleClickPlayTrack: MouseEventHandler<HTMLDivElement> = (event) => {
    const { nativeEvent } = event;
    const { offsetX } = nativeEvent;
    setTime(timeScale.invert(offsetX));
    pause();
  };

  /** set time when dragging playhead */
  const playheadRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const playheadElement = playheadRef.current;

    if (playheadElement) {
      const range = timeScale.range();

      select<HTMLDivElement, null>(playheadElement).call(
        drag<HTMLDivElement, null>().on("drag", function (event: DragDivElement) {
          const { x } = event;

          const xPosClamped = clamp(x, range[0] || 0, range[1] || 0);

          setTime(timeScale.invert(xPosClamped));
        })
      );
    }
  }, [timeScale, setTime]);

  return (
    <div className={styles.playControl}>
      <div ref={trackContainerRef} className={styles.playTrack}>
        <div className={styles.playTrackBackground} onMouseDown={handleClickPlayTrack} />
        <div ref={playheadRef} className={styles.playhead} style={{ left: `${timeScale(time)}px` }} />
      </div>
    </div>
  );
};
